// ABOUTME: Builds the list of AppBar nav items the current user may see, with role checks,
// ABOUTME: feature flags, labels and disabled state already resolved.

import {
    AccountTreeIcon,
    AccountTreeOutlinedIcon,
    AssessmentIcon,
    AssessmentOutlinedIcon,
    BusinessCenterIcon,
    BusinessCenterOutlinedIcon,
    CloudSyncIcon,
    CloudSyncOutlinedIcon,
    DashboardIcon,
    DashboardOutlinedIcon,
    DescriptionIcon,
    DescriptionOutlinedIcon,
    ExtensionIcon,
    ExtensionOutlinedIcon,
    LanguageIcon,
    LanguageOutlinedIcon,
    ListIcon,
    ListOutlinedIcon,
    LocalOfferIcon,
    LocalOfferOutlinedIcon,
    SearchIcon,
    SearchOutlinedIcon,
    SettingsIcon,
    SettingsOutlinedIcon,
    TranslateIcon,
    TranslateOutlinedIcon,
    ViewInArIcon,
    ViewInArOutlinedIcon,
    WorkIcon,
    WorkOutlineOutlinedIcon,
} from '@ds/coolshop';
import { useLanguageCtx } from '@modules/language';
import { UI_APPBAR_ROLES } from '@src/_new/modules/auth/appBarRoles';
import { useCanAccessHierarchies } from '@src/_new/modules/auth/canAccessHierarchies';
import { CPQ_PRICING_DEBUG_ROLE } from '@src/_new/modules/cpq/components/PricingTrace';
import { useCustomPages } from '@src/_new/modules/custom-pages/hooks/useCustomPages';
import { resolveIcon } from '@src/_new/modules/custom-pages/utils/resolveIcon';
import { useCompanyOverrides } from '@src/_new/overrides/hooks/useCompanyOverrides';
import {
    path as translationsPath,
    useTranslations,
} from '@src/components/translations/Translations';
import { useAuth } from '@src/hooks';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { toTitleCase } from '@src/utils';
import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { NavCandidate } from '../types';

type Row = { visible: boolean; candidate: Omit<NavCandidate, 'defaultIndex'> };

/**
 * One row per nav item the AppBar can render, in the built-in order. Rows whose condition is false
 * are dropped, and the survivors are numbered by `defaultIndex` — the built-in position the layout
 * resolver falls back to.
 */
export function useNavCandidates(): NavCandidate[] {
    const intl = useIntl();
    const { defaultLanguage } = useLanguageCtx();
    const {
        enableSearch,
        jsonataDependenciesEnabled,
        enableExternalCallLog,
        variantPricingEnabled,
    } = useContext(SettingsContext);
    const { hasUserRole } = useAuth();
    const { canTranslate } = useTranslations();
    const { menuItems: companyMenuItems } = useCompanyOverrides();
    const customPages = useCustomPages();
    const canAccessHierarchies = useCanAccessHierarchies();

    return useMemo(() => {
        const hasLanguages = !!defaultLanguage;

        // The label a nav item gets when no explicit one is passed, matching LinkTo's fallback.
        const auto = (dest: string) =>
            intl.formatMessage({ id: dest, defaultMessage: toTitleCase(dest) });

        const rows: Row[] = [
            {
                // Never disabled: it is where a tenant without languages goes to create one.
                visible: hasUserRole(UI_APPBAR_ROLES.LANGUAGE),
                candidate: {
                    id: 'language',
                    dest: 'language',
                    label: auto('language'),
                    icon: <LanguageOutlinedIcon />,
                    activeIcon: <LanguageIcon />,
                    disabled: false,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.PRODUCTS),
                candidate: {
                    id: 'products',
                    dest: 'products',
                    label: auto('products'),
                    icon: <BusinessCenterOutlinedIcon />,
                    activeIcon: <BusinessCenterIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.VIEWER_3D),
                candidate: {
                    id: '3d-viewer',
                    dest: '3d-viewer',
                    label: auto('3d-viewer'),
                    icon: <ViewInArOutlinedIcon />,
                    activeIcon: <ViewInArIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.HIERARCHIES) && canAccessHierarchies,
                candidate: {
                    id: 'hierarchies',
                    dest: 'hierarchies',
                    label: auto('hierarchies'),
                    icon: <AccountTreeOutlinedIcon />,
                    activeIcon: <AccountTreeIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.JOBS),
                candidate: {
                    id: 'jobs',
                    dest: 'jobs',
                    label: auto('jobs'),
                    // WorkOutlined is almost solid: WorkOutline is the hollow briefcase.
                    icon: <WorkOutlineOutlinedIcon />,
                    activeIcon: <WorkIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                // Never disabled.
                visible: canTranslate && hasUserRole(UI_APPBAR_ROLES.TRANSLATIONS),
                candidate: {
                    id: translationsPath,
                    dest: translationsPath,
                    label: auto(translationsPath),
                    icon: <TranslateOutlinedIcon />,
                    activeIcon: <TranslateIcon />,
                    disabled: false,
                },
            },
            {
                visible: enableSearch && hasUserRole(UI_APPBAR_ROLES.SEARCH),
                candidate: {
                    id: 'search',
                    dest: 'search',
                    label: auto('search'),
                    icon: <SearchOutlinedIcon />,
                    activeIcon: <SearchIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.DISCOUNTS),
                candidate: {
                    id: 'discounts',
                    dest: 'discounts',
                    label: auto('discounts'),
                    icon: <LocalOfferOutlinedIcon />,
                    activeIcon: <LocalOfferIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                // The underscore is the built-in route segment, so it is also the config id.
                visible: hasUserRole(UI_APPBAR_ROLES.SHORT_LISTS),
                candidate: {
                    id: 'short_lists',
                    dest: 'short_lists',
                    label: auto('short_lists'),
                    icon: <ListOutlinedIcon />,
                    activeIcon: <ListIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.DATA_QUALITY),
                candidate: {
                    id: 'data-quality',
                    dest: 'data-quality',
                    label: auto('data-quality'),
                    icon: <AssessmentOutlinedIcon />,
                    activeIcon: <AssessmentIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.OFFERS),
                candidate: {
                    id: 'offers',
                    dest: 'offers',
                    label: auto('offers'),
                    icon: <DescriptionOutlinedIcon />,
                    activeIcon: <DescriptionIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(UI_APPBAR_ROLES.SETTINGS),
                candidate: {
                    id: 'settings',
                    dest: 'settings',
                    label: auto('settings'),
                    icon: <SettingsOutlinedIcon />,
                    activeIcon: <SettingsIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                // Never disabled.
                visible:
                    hasUserRole('PIM_SUPERADMIN') && hasUserRole(UI_APPBAR_ROLES.ADMIN_DASHBOARD),
                candidate: {
                    id: 'admin-dashboard',
                    dest: 'admin-dashboard',
                    label: intl.formatMessage({
                        id: 'nav.admin-dashboard',
                        defaultMessage: 'Admin Dashboard',
                    }),
                    icon: <DashboardOutlinedIcon />,
                    activeIcon: <DashboardIcon />,
                    disabled: false,
                },
            },
            {
                // Never disabled.
                visible: hasUserRole('COMPANY_ADMIN') && hasUserRole(UI_APPBAR_ROLES.COMPANY_ADMIN),
                candidate: {
                    id: 'company-admin',
                    dest: 'company-admin',
                    label: intl.formatMessage({
                        id: 'nav.company-admin',
                        defaultMessage: 'My Company',
                    }),
                    icon: <DashboardOutlinedIcon />,
                    activeIcon: <DashboardIcon />,
                    disabled: false,
                },
            },
            {
                // Never disabled.
                visible: hasUserRole('PIM_CONFIGIT_ADMIN'),
                candidate: {
                    id: 'configit',
                    dest: 'configit',
                    label: intl.formatMessage({ id: 'nav.configit', defaultMessage: 'Configit' }),
                    icon: <ExtensionOutlinedIcon />,
                    activeIcon: <ExtensionIcon />,
                    disabled: false,
                },
            },
            {
                visible: hasUserRole('PIM_ADMIN') && jsonataDependenciesEnabled === true,
                candidate: {
                    id: 'jsonata-dependencies',
                    dest: 'jsonata-dependencies',
                    label: intl.formatMessage({
                        id: 'nav.jsonata-dependencies',
                        defaultMessage: 'JSONata Dependencies',
                    }),
                    icon: <AccountTreeOutlinedIcon />,
                    activeIcon: <AccountTreeIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole('PIM_ADMIN') && enableExternalCallLog === true,
                candidate: {
                    id: 'external-call-log',
                    dest: 'external-call-log',
                    label: intl.formatMessage({
                        id: 'nav.external-call-log',
                        defaultMessage: 'External Calls',
                    }),
                    icon: <CloudSyncOutlinedIcon />,
                    activeIcon: <CloudSyncIcon />,
                    disabled: !hasLanguages,
                },
            },
            {
                visible: hasUserRole(CPQ_PRICING_DEBUG_ROLE) && variantPricingEnabled === true,
                candidate: {
                    id: 'pricing-test-bench',
                    dest: 'pricing-test-bench',
                    label: intl.formatMessage({
                        id: 'nav.pricing-test-bench',
                        defaultMessage: 'Pricing Test Bench',
                    }),
                    icon: <AssessmentOutlinedIcon />,
                    activeIcon: <AssessmentIcon />,
                    disabled: !hasLanguages,
                },
            },
            ...customPages
                .filter(page => !page.hideInNavbar)
                .map(page => ({
                    visible: true,
                    candidate: {
                        id: `page:${page.id}`,
                        dest: `pages/${page.slug}`,
                        label: page.name,
                        icon: resolveIcon(page.icon),
                        disabled: !hasLanguages,
                    },
                })),
            // Company menu items are already role-filtered and order-sorted by useCompanyOverrides.
            ...companyMenuItems.map(item => ({
                visible: true,
                candidate: {
                    id: `company:${item.id}`,
                    dest: item.path,
                    label: auto(item.path),
                    icon: item.icon,
                    disabled: !hasLanguages,
                },
            })),
        ];

        return rows
            .filter(row => row.visible)
            .map((row, index) => ({ ...row.candidate, defaultIndex: index }));
    }, [
        intl,
        hasUserRole,
        enableSearch,
        jsonataDependenciesEnabled,
        enableExternalCallLog,
        variantPricingEnabled,
        defaultLanguage,
        canTranslate,
        customPages,
        companyMenuItems,
        canAccessHierarchies,
    ]);
}
